<script setup lang="ts">
import Button from 'primevue/button';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, ref } from 'vue';
import { useConfigureDebugUser } from '@/composables/useDebugUser';

// Only show in development
if (!import.meta.env.DEV) {
  defineOptions({
    render: () => null,
  });
}

const { currentUserId, isDebugMode, setDebugUserId, clearDebugUser, actualUser } = useConfigureDebugUser();
const toast = useToast();

const newUserId = ref('');
const isVisible = ref(true);

// Quick user IDs for common debugging scenarios
const quickUsers = ref([
  { id: 'ZukuK7CAYrVOevS8NsmRUPN786p2', label: 'Expired acc' },
  { id: 'mYZevaoABwXrZIcG2QSr6uUr6Dq2', label: 'Main Acc' },
]);

// Load saved debug users from localStorage
onMounted(() => {
  const saved = localStorage.getItem('debug-quick-users');
  if (saved) {
    try {
      quickUsers.value = JSON.parse(saved);
    }
    catch {
      console.warn('Failed to load saved debug users');
    }
  }
});

function saveQuickUsers() {
  localStorage.setItem('debug-quick-users', JSON.stringify(quickUsers.value));
}

function setDebugUser() {
  if (!newUserId.value.trim()) {
    toast.add({
      severity: 'warn',
      summary: 'Debug Mode',
      detail: 'Please enter a user ID',
      life: 3000,
    });
    return;
  }

  setDebugUserId(newUserId.value.trim());

  // Add to quick users if not already there
  const userId = newUserId.value.trim();
  if (!quickUsers.value.some(u => u.id === userId)) {
    quickUsers.value.push({ id: userId, label: `User ${userId}` });
    saveQuickUsers();
  }

  toast.add({
    severity: 'info',
    summary: 'Debug Mode',
    detail: `Switched to debug user: ${userId}`,
    life: 3000,
  });

  newUserId.value = '';
}

function useQuickUser(userId: string) {
  setDebugUserId(userId);
  toast.add({
    severity: 'info',
    summary: 'Debug Mode',
    detail: `Switched to debug user: ${userId}`,
    life: 3000,
  });
}

function clearDebug() {
  clearDebugUser();
  toast.add({
    severity: 'success',
    summary: 'Debug Mode',
    detail: 'Returned to actual user',
    life: 3000,
  });
}

function removeQuickUser(userId: string) {
  quickUsers.value = quickUsers.value.filter(u => u.id !== userId);
  saveQuickUsers();
}

function toggleVisibility() {
  isVisible.value = !isVisible.value;
}

const statusText = computed(() => {
  if (isDebugMode.value) {
    return `Debug: ${currentUserId.value}`;
  }
  return actualUser.value ? `Real: ${actualUser.value.uid}` : 'Not logged in';
});

const isDev = import.meta.env.DEV;
</script>

<template>
  <div
    v-if="isDev"
    class="fixed bottom-4 right-4 z-50 max-w-sm"
  >
    <!-- Minimized state -->
    <div
      v-if="!isVisible"
      class="bg-orange-500 text-white px-3 py-2 rounded-full shadow-lg cursor-pointer hover:bg-orange-600 transition-colors"
      @click="toggleVisibility"
    >
      <i class="pi pi-user text-sm mr-2" />
      <span class="text-sm font-medium">Debug</span>
    </div>

    <!-- Expanded state -->
    <Card
      v-else
      class="shadow-2xl border-2 border-orange-400"
    >
      <template #title>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <i class="pi pi-bug text-orange-500" />
            <span class="text-orange-700 text-sm font-bold">Debug User Switcher</span>
          </div>
          <Button
            icon="pi pi-minus"
            text
            size="small"
            class="p-1"
            @click="toggleVisibility"
          />
        </div>
      </template>

      <template #content>
        <div class="space-y-3">
          <!-- Current status -->
          <div class="p-2 rounded border-l-4 text-sm" :class="isDebugMode ? 'border-orange-400 bg-orange-50' : 'border-green-400 bg-green-50'">
            <div class="font-medium" :class="isDebugMode ? 'text-orange-700' : 'text-green-700'">
              {{ statusText }}
            </div>
          </div>

          <!-- Quick users -->
          <div v-if="quickUsers.length > 0">
            <div class="text-xs font-medium text-gray-600 mb-1">
              Quick Switch:
            </div>
            <div class="flex flex-wrap gap-1">
              <Button
                v-for="user in quickUsers"
                :key="user.id"
                :label="user.label"
                size="small"
                outlined
                class="text-xs"
                :title="`Click to use ${user.id}, right-click to remove`"
                @click="useQuickUser(user.id)"
                @contextmenu.prevent="removeQuickUser(user.id)"
              />
            </div>
          </div>

          <!-- Manual user ID input -->
          <div class="space-y-2">
            <div class="text-xs font-medium text-gray-600">
              Switch to User ID:
            </div>
            <div class="flex gap-2">
              <InputText
                v-model="newUserId"
                placeholder="Enter user ID"
                size="small"
                class="flex-1"
                @keyup.enter="setDebugUser"
              />
              <Button
                label="Set"
                size="small"
                @click="setDebugUser"
              />
            </div>
          </div>

          <!-- Actions -->
          <div class="flex gap-2">
            <Button
              v-if="isDebugMode"
              label="Clear Debug"
              size="small"
              severity="success"
              outlined
              @click="clearDebug"
            />
          </div>

          <!-- Help text -->
          <div class="text-xs text-gray-500 border-t pt-2">
            <div>• Right-click quick users to remove</div>
            <div>• This only works in development</div>
            <div>• All API calls will use debug user ID</div>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
/* Ensure the component stays on top */
.fixed {
  z-index: 9999;
}
</style>
